import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

// Separate contexts to prevent writer-only consumers from re-rendering
// when the actions value changes
const BreadcrumbActionsContext = createContext<React.ReactNode>(null);
const BreadcrumbSetterContext = createContext<
  (actions: React.ReactNode) => void
>(() => {});

export const BreadcrumbActionsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [actions, setActions] = useState<React.ReactNode>(null);

  return (
    <BreadcrumbSetterContext.Provider value={setActions}>
      <BreadcrumbActionsContext.Provider value={actions}>
        {children}
      </BreadcrumbActionsContext.Provider>
    </BreadcrumbSetterContext.Provider>
  );
};

/**
 * Hook to read the current breadcrumb actions.
 * Used by the layout/Breadcrumb component.
 */
export const useBreadcrumbActions = (): React.ReactNode => {
  return useContext(BreadcrumbActionsContext);
};

/**
 * Hook to set breadcrumb actions from any page component.
 * Returns a stable setter. Automatically clears actions on unmount.
 */
export const useSetBreadcrumbActions = (): ((
  actions: React.ReactNode
) => void) => {
  const setActions = useContext(BreadcrumbSetterContext);

  // Clear on unmount
  useEffect(() => {
    return () => {
      setActions(null);
    };
  }, [setActions]);

  return useCallback(
    (actions: React.ReactNode) => {
      setActions(actions);
    },
    [setActions]
  );
};
